import { IReactionDisposer, reaction } from "mobx";
import React from "react";
import { FormattedMessage } from "react-intl";

import { LinkBlockModel } from "@courses-models/course";

import { Block } from "@apps/viewer/src/ts/components/block/Block";
import { colors } from "@apps/viewer/src/ts/config";
import { injectStore, Store } from "@apps/viewer/src/ts/store";

type LinkBlockProps = {
    /**
     * A reference to the store that's backing the application lifecycle state.
     */
    store: Store;

    /**
     * A reference to the model representing the block that's currently being
     * rendered.
     */
    model: LinkBlockModel;
};

type LinkBlockState = {
    isMobile: boolean;
};

/**
 * Renders external links (ie. a button to open the defined link in a new window
 * along with an optional description of the link).
 */
class LinkBlockUnwrapped extends Block<LinkBlockProps, LinkBlockState> {
    /**
     * Setup internal state
     */
    public state: LinkBlockState = {
        isMobile: this.props.store.isMobile,
    };

    /**
     * Contains a reference to a disposer that'll dispose of the isMobile
     * reaction.
     */
    protected reactionDisposer: IReactionDisposer;

    /**
     * Once the component mounts, create a reaction to isMobile changes, which
     * will update isMobile to the components internal state.
     * This is done since LinkBlock extends from Block which is a PureComponent.
     */
    public componentDidMount(): void {
        this.reactionDisposer = reaction(
            () => this.props.store.isMobile,
            (isMobile) => {
                this.setState({ isMobile });
            },
        );
    }

    /**
     * Dispose of the reaction once the component unmounts
     */
    public componentWillUnmount(): void {
        this.reactionDisposer();
    }

    /**
     * Renderer that injects the content as entered by the user himself.
     */
    protected renderContent(): React.JSX.Element {
        return (
            <div>
                <p className={`${this.state.isMobile ? "mobile" : ""}`}>{this.props.model.description}</p>
                {this.renderLink()}

                <style jsx>{`
                    p {
                        /** Override defaults */
                        margin: 22px 0 30px;

                        /** Setup font */
                        font-size: 18px;
                        line-height: 1.5;

                        &.mobile {
                            /** Slightly reduce font-size on mobile */
                            font-size: 16px;
                        }
                    }
                `}</style>
            </div>
        );
    }

    /**
     * Rendered that'll render the link of the block
     */
    protected renderLink(): React.JSX.Element {
        const href = this.props.model.href.includes("://")
            ? this.props.model.href
            : `https://${this.props.model.href}`;

        return (
            <a
                href={href}
                role="button"
                target="_blank"
                rel="noopener noreferrer"
            >
                <FormattedMessage
                    id="Block__link__open"
                    defaultMessage="Åbn link"
                />

                <style jsx>{`
                    a {
                        /** Override defaults */
                        text-decoration: none;

                        /**
                         * setup dimensions of link
                         */
                        display: inline-block;
                        width: auto;
                        height: 41px;
                        line-height: 41px;
                        box-sizing: content-box;

                        /** Setup internal sizing */
                        padding: 0 45px;

                        /** Render link border */
                        border: 2px solid ${ colors.orange};

                        /** Setup font */
                        font-size: 18px;
                        font-weight: 500;
                        text-transform: uppercase;
                        color: ${ colors.darkGrey};

                        /** Prepare hover transitions */
                        transition: color 0.1s linear, background-color 0.1s linear;

                        &:hover {
                            background-color: ${ colors.orange};
                            color: ${ colors.white};
                        }
                    }
                `}</style>
            </a>
        );
    }
}

// tslint:disable-next-line variable-name
export const LinkBlock = injectStore(LinkBlockUnwrapped);
